import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { uploadImage, fullUrl } from "../lib/uploads";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle, Clock, X as XIcon, Camera, MagnifyingGlass, Plus,
  FacebookLogo, InstagramLogo, Globe,
} from "@phosphor-icons/react";

const YEARS_OPTIONS = [
  { value: "", label: "Select…" },
  { value: "0-1", label: "Less than 1 year" },
  { value: "1-2", label: "1–2 years" },
  { value: "3-5", label: "3–5 years" },
  { value: "6-10", label: "6–10 years" },
  { value: "10+", label: "10+ years" },
];

const MAX_TRADES = 8;
const MAX_PORTFOLIO = 12;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [tradeQuery, setTradeQuery] = useState("");
  const [areaInput, setAreaInput] = useState("");

  useEffect(() => {
    api.get("/meta/trades").then(({ data }) => setTrades(data || []))
      .catch((err) => console.error("[profile] failed to load trades:", err));
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      business_name: user.business_name || "",
      bio: user.bio || "",
      phone: user.phone || "",
      postcode: user.postcode || "",
      work_range_miles: user.work_range_miles ?? 10,
      trades: user.trades || [],
      portfolio: user.portfolio || [],
      picture: user.picture || null,
      cover_photo: user.cover_photo || null,
      years_experience: user.years_experience || "",
      qualifications: user.qualifications || "",
      service_areas: user.service_areas || [],
      social_links: user.social_links || {},
    });
  }, [user]);

  const filteredTrades = useMemo(() => {
    const q = tradeQuery.trim().toLowerCase();
    if (!q) return trades;
    return trades.filter((t) => t.name.toLowerCase().includes(q));
  }, [trades, tradeQuery]);

  const selectedTradeObjects = useMemo(
    () => (form.trades || []).map((slug) => trades.find((t) => t.slug === slug)).filter(Boolean),
    [trades, form.trades],
  );

  const toggleTrade = (slug) => {
    const has = (form.trades || []).includes(slug);
    if (!has && (form.trades || []).length >= MAX_TRADES) {
      toast.error(`You can select up to ${MAX_TRADES} services. Remove one first.`);
      return;
    }
    setForm((f) => ({
      ...f,
      trades: has ? f.trades.filter((x) => x !== slug) : [...f.trades, slug],
    }));
  };

  const onPicture = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await uploadImage(f);
      setForm((s) => ({ ...s, picture: url }));
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    }
  };

  const onCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await uploadImage(f);
      setForm((s) => ({ ...s, cover_photo: url }));
      toast.success("Cover photo updated");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    }
  };

  const onPortfolio = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_PORTFOLIO - (form.portfolio || []).length;
    if (remaining <= 0) {
      toast.error(`Portfolio is full (max ${MAX_PORTFOLIO}). Remove one to add more.`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    try {
      const urls = [];
      for (const f of toUpload) {
        const url = await uploadImage(f);
        urls.push(url);
      }
      setForm((s) => ({ ...s, portfolio: [...(s.portfolio || []), ...urls].slice(0, MAX_PORTFOLIO) }));
      toast.success(`${urls.length} photo${urls.length === 1 ? "" : "s"} added`);
    } catch (err) {
      toast.error(err.message || "Some photos failed to upload");
    }
  };

  const addServiceArea = () => {
    const a = areaInput.trim().toUpperCase().replace(/\s+/g, "");
    if (!a) return;
    if ((form.service_areas || []).includes(a)) {
      toast.info("Already added");
      return;
    }
    if ((form.service_areas || []).length >= 20) {
      toast.error("Maximum 20 service areas");
      return;
    }
    setForm((s) => ({ ...s, service_areas: [...(s.service_areas || []), a] }));
    setAreaInput("");
  };

  const removeServiceArea = (a) => {
    setForm((s) => ({ ...s, service_areas: s.service_areas.filter((x) => x !== a) }));
  };

  const setSocial = (key, value) => {
    setForm((s) => ({ ...s, social_links: { ...(s.social_links || {}), [key]: value } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/me/profile", form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;
  const isTrader = user.role === "tradesperson";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 py-8 sm:py-10">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--brand-green)" }}>
          Your profile
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {isTrader
            ? "A complete profile = more trust = more jobs. Add photos, services, and your bio."
            : "Keep your details up to date so trades can reach you fast."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-5">
          {/* Cover + profile photo card */}
          <div className="brand-card overflow-hidden p-0">
            <div className="relative h-32 sm:h-40" style={{ background: form.cover_photo ? `center/cover no-repeat url(${fullUrl(form.cover_photo)})` : "linear-gradient(135deg, #11523B 0%, #1a6f4e 100%)" }}>
              <label className="absolute right-3 top-3 bg-white/95 text-[var(--brand-green)] px-3 py-1.5 rounded-md text-xs font-bold cursor-pointer flex items-center gap-1 shadow" data-testid="cover-upload">
                <Camera size={14} weight="fill" /> {form.cover_photo ? "Change cover" : "Add cover"}
                <input type="file" accept="image/*" className="hidden" onChange={onCover} />
              </label>
            </div>
            <div className="px-5 sm:px-6 pb-5 -mt-12 sm:-mt-14">
              <div className="flex items-end gap-4">
                <div className="relative">
                  {form.picture ? (
                    <img src={fullUrl(form.picture)} alt="" className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border-4 border-white shadow" />
                  ) : (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-[rgba(17,82,59,0.1)] text-[var(--brand-green)] font-display font-bold text-3xl flex items-center justify-center border-4 border-white shadow">
                      {(form.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <label className="absolute -bottom-1 -right-1 bg-[var(--brand-orange)] text-white p-1.5 rounded-full cursor-pointer shadow" data-testid="profile-pic-upload">
                    <Camera size={14} weight="fill" />
                    <input type="file" accept="image/*" className="hidden" onChange={onPicture} />
                  </label>
                </div>
                <div className="pb-2">
                  <div className="font-display font-extrabold text-lg sm:text-xl text-[var(--brand-green)]">
                    {form.business_name || form.name || "Your profile"}
                  </div>
                  {user.verified && (
                    <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1 mt-0.5">
                      <CheckCircle size={14} weight="fill" /> Verified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Basics card */}
          <div className="brand-card p-5 sm:p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" required>
                <Input data-testid="profile-name-input" value={form.name || ""} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
              </Field>
              <Field label="Phone">
                <Input data-testid="profile-phone-input" value={form.phone || ""} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} placeholder="07700 900000" />
              </Field>
            </div>

            {isTrader && (
              <>
                <Field label="Business name">
                  <Input data-testid="profile-business-input" value={form.business_name || ""} onChange={(e) => setForm((s) => ({ ...s, business_name: e.target.value }))} placeholder="e.g. Adeel Plastering" />
                </Field>
                <Field label="Bio · Tell customers about your work">
                  <textarea data-testid="profile-bio-input" rows={4} value={form.bio || ""} onChange={(e) => setForm((s) => ({ ...s, bio: e.target.value }))} maxLength={500}
                    className="w-full px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white resize-y" placeholder="Hi, I'm…" />
                  <span className="text-xs text-[var(--text-muted)] mt-1 block">{(form.bio || "").length}/500</span>
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Years of experience">
                    <select data-testid="profile-years-input" value={form.years_experience || ""} onChange={(e) => setForm((s) => ({ ...s, years_experience: e.target.value }))}
                      className="w-full px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white">
                      {YEARS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Qualifications / Registrations">
                    <Input data-testid="profile-quals-input" value={form.qualifications || ""} onChange={(e) => setForm((s) => ({ ...s, qualifications: e.target.value }))} placeholder="e.g. Gas Safe 123456, NICEIC" />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Base postcode" required>
                    <Input data-testid="profile-postcode-input" value={form.postcode || ""} onChange={(e) => setForm((s) => ({ ...s, postcode: e.target.value }))} placeholder="HD2 1AT" />
                  </Field>
                  <Field label={`Work range: ${form.work_range_miles} miles`}>
                    <input data-testid="profile-range-slider" type="range" min={0} max={50} value={form.work_range_miles || 0}
                      onChange={(e) => setForm((s) => ({ ...s, work_range_miles: Number(e.target.value) }))}
                      className="w-full mt-3 accent-[var(--brand-orange)]" />
                  </Field>
                </div>
              </>
            )}
          </div>

          {/* Services picker — trade only */}
          {isTrader && (
            <div className="brand-card p-5 sm:p-6" data-testid="services-picker">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-display font-extrabold text-lg text-[var(--brand-green)]">Your services</h2>
                <span className="text-xs font-semibold text-[var(--text-muted)]">
                  {(form.trades || []).length}/{MAX_TRADES} selected
                </span>
              </div>
              <p className="text-sm text-[var(--text-muted)] mt-1">
                Pick the services you offer. You&apos;ll get job alerts for all of them.
              </p>

              {/* Selected chips */}
              {selectedTradeObjects.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="selected-services">
                  {selectedTradeObjects.map((t) => (
                    <span key={t.slug} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-sm font-semibold"
                          style={{ background: "var(--brand-green)", color: "#fff" }}>
                      {t.name}
                      <button type="button" onClick={() => toggleTrade(t.slug)} className="bg-white/20 hover:bg-white/30 rounded-full p-0.5" aria-label={`Remove ${t.name}`}>
                        <XIcon size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Search */}
              <div className="mt-4 relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  data-testid="trade-search"
                  value={tradeQuery}
                  onChange={(e) => setTradeQuery(e.target.value)}
                  placeholder="Search services…"
                  className="w-full pl-10 pr-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white"
                />
              </div>

              {/* Trade list */}
              <div className="mt-3 max-h-80 overflow-y-auto -mx-1 px-1" data-testid="trade-list">
                {filteredTrades.length === 0 ? (
                  <p className="py-6 text-center text-sm text-[var(--text-muted)]">No services match &quot;{tradeQuery}&quot;</p>
                ) : (
                  filteredTrades.map((t) => {
                    const checked = (form.trades || []).includes(t.slug);
                    return (
                      <button
                        type="button"
                        key={t.slug}
                        onClick={() => toggleTrade(t.slug)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg my-0.5 transition-colors text-left ${
                          checked ? "bg-[#F4FDF7]" : "hover:bg-[#FAF8F2]"
                        }`}
                        data-testid={`trade-row-${t.slug}`}
                      >
                        <span className="text-sm font-semibold text-[#1F4733]">{t.name}</span>
                        <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          checked ? "bg-[var(--brand-orange)] border-[var(--brand-orange)]" : "border-[var(--border-stone)]"
                        }`}>
                          {checked && <CheckCircle size={14} weight="fill" className="text-white" />}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Service areas */}
          {isTrader && (
            <div className="brand-card p-5 sm:p-6">
              <h2 className="font-display font-extrabold text-lg text-[var(--brand-green)]">Service areas</h2>
              <p className="text-sm text-[var(--text-muted)] mt-1">
                Add outward postcodes you cover (e.g. <code>HD2</code>, <code>WF1</code>). Optional — work range above is the main filter.
              </p>
              <div className="mt-4 flex gap-2">
                <input
                  value={areaInput}
                  onChange={(e) => setAreaInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addServiceArea(); } }}
                  placeholder="HD2"
                  className="flex-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white uppercase"
                  data-testid="service-area-input"
                />
                <button type="button" onClick={addServiceArea} className="btn-ghost" data-testid="service-area-add">
                  <Plus size={16} weight="bold" /> Add
                </button>
              </div>
              {(form.service_areas || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {form.service_areas.map((a) => (
                    <span key={a} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold bg-[#F4FDF7] text-[var(--brand-green)] border border-[#CBE7D6]">
                      {a}
                      <button type="button" onClick={() => removeServiceArea(a)} aria-label={`Remove ${a}`}>
                        <XIcon size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Portfolio */}
          {isTrader && (
            <div className="brand-card p-5 sm:p-6">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-display font-extrabold text-lg text-[var(--brand-green)]">Work portfolio</h2>
                <span className="text-xs font-semibold text-[var(--text-muted)]">
                  {(form.portfolio || []).length}/{MAX_PORTFOLIO}
                </span>
              </div>
              <p className="text-sm text-[var(--text-muted)] mt-1">Add photos of past jobs — homeowners trust profiles with visible work.</p>
              <label className="mt-4 block border-2 border-dashed border-[var(--border-stone)] rounded-md p-5 text-center cursor-pointer hover:bg-[#FAF8F2]" data-testid="portfolio-upload">
                <Camera size={22} weight="duotone" className="mx-auto text-[var(--brand-green)]" />
                <span className="block text-sm font-semibold mt-2">Tap to add photos</span>
                <span className="text-xs text-[var(--text-muted)]">JPG/PNG/WebP/HEIC · Up to 8 MB each</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={onPortfolio} />
              </label>
              {(form.portfolio || []).length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {form.portfolio.map((p, i) => (
                    <div key={p} className="relative group">
                      <img src={fullUrl(p)} alt="" className="w-full h-24 sm:h-28 object-cover rounded-md border border-[var(--border-stone)]" />
                      <button type="button" onClick={() => setForm((s) => ({ ...s, portfolio: s.portfolio.filter((_, idx) => idx !== i) }))}
                        className="absolute top-1 right-1 bg-white/95 text-[var(--brand-green)] rounded-md w-6 h-6 flex items-center justify-center text-xs shadow hover:bg-white"
                        data-testid={`portfolio-remove-${i}`} aria-label="Remove">
                        <XIcon size={12} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Social links */}
          {isTrader && (
            <div className="brand-card p-5 sm:p-6">
              <h2 className="font-display font-extrabold text-lg text-[var(--brand-green)]">Social &amp; web (optional)</h2>
              <p className="text-sm text-[var(--text-muted)] mt-1">Helps customers do a quick sanity-check on your business.</p>
              <div className="mt-4 space-y-3">
                <SocialField icon={<Globe size={18} weight="duotone" className="text-[var(--brand-green)]" />} label="Website" placeholder="https://yourbusiness.co.uk"
                  value={form.social_links?.website || ""} onChange={(v) => setSocial("website", v)} testid="profile-website" />
                <SocialField icon={<FacebookLogo size={18} weight="duotone" className="text-[var(--brand-green)]" />} label="Facebook" placeholder="https://facebook.com/yourbiz"
                  value={form.social_links?.facebook || ""} onChange={(v) => setSocial("facebook", v)} testid="profile-facebook" />
                <SocialField icon={<InstagramLogo size={18} weight="duotone" className="text-[var(--brand-green)]" />} label="Instagram" placeholder="https://instagram.com/yourbiz"
                  value={form.social_links?.instagram || ""} onChange={(v) => setSocial("instagram", v)} testid="profile-instagram" />
              </div>
            </div>
          )}

          <button disabled={busy} className="btn-primary w-full justify-center py-3.5" data-testid="profile-save-btn">
            {busy ? "Saving…" : "Save changes"}
          </button>
          {isTrader && (
            <button type="button" onClick={() => navigate(`/trades/${user.user_id}`)}
                    className="btn-ghost w-full justify-center" data-testid="view-public-profile">
              Preview my public profile
            </button>
          )}
        </form>

        {isTrader && <VerificationSection />}

        <DangerZone />
      </div>
    </Layout>
  );
}

function DangerZone() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const canDelete = confirmText.trim().toUpperCase() === "DELETE";

  const doDelete = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.delete("/auth/me");
      toast.success("Your account has been permanently deleted.");
      try { await logout(); } catch { /* already gone */ }
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <section className="mt-8 rounded-xl border-2 p-5" style={{ borderColor: "#F5C2C0", background: "#FEF6F5" }} data-testid="danger-zone">
      <h3 className="font-display font-bold text-lg" style={{ color: "#7A1F1B" }}>Delete my account</h3>
      <p className="mt-1 text-sm text-[var(--text-muted)] leading-relaxed">
        Permanently deletes your profile, jobs, quotes, messages, and reviews. This cannot be undone.
      </p>
      {!showConfirm ? (
        <button
          onClick={() => setShowConfirm(true)}
          className="mt-3 px-4 py-2 rounded-md font-semibold border-2 hover:bg-[#FEE2E2] transition-colors"
          style={{ borderColor: "#DC2626", color: "#7A1F1B" }}
          data-testid="delete-account-btn"
        >
          Delete my account…
        </button>
      ) : (
        <div className="mt-3 space-y-3">
          <label className="block text-sm">
            <span className="font-semibold" style={{ color: "#7A1F1B" }}>
              Type <code className="bg-white px-1.5 py-0.5 rounded border">DELETE</code> to confirm:
            </span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-md border-2 bg-white uppercase font-mono focus:outline-none"
              style={{ borderColor: "#DC2626" }}
              placeholder="DELETE"
              data-testid="delete-confirm-input"
            />
          </label>
          <div className="flex gap-2">
            <button
              onClick={doDelete}
              disabled={!canDelete || busy}
              className="px-4 py-2 rounded-md font-bold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "#DC2626" }}
              data-testid="delete-account-confirm-btn"
            >
              {busy ? "Deleting…" : "Permanently delete"}
            </button>
            <button
              onClick={() => { setShowConfirm(false); setConfirmText(""); }}
              disabled={busy}
              className="px-4 py-2 rounded-md font-semibold border-2 hover:bg-white transition-colors"
              style={{ borderColor: "#94A3B8", color: "#475569" }}
              data-testid="delete-account-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[var(--text-muted)]">
        {label} {required && <span className="text-[var(--brand-orange)]">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white focus:border-[var(--brand-green)] focus:ring-2 focus:ring-[var(--brand-green)]/10 outline-none transition"
    />
  );
}

function SocialField({ icon, label, placeholder, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-md flex items-center justify-center bg-[#F4FDF7] flex-shrink-0">{icon}</span>
      <div className="flex-1">
        <span className="text-xs font-semibold text-[var(--text-muted)] block">{label}</span>
        <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      </div>
    </div>
  );
}

function VerificationSection() {
  const [current, setCurrent] = useState(null);
  const [idDoc, setIdDoc] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [regNumber, setRegNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/verifications/mine");
      setCurrent(data && data.status !== "none" ? data : null);
    } catch (err) {
      console.error("[verification] failed to load:", err);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!idDoc) { toast.error("Please upload your ID document"); return; }
    setBusy(true);
    try {
      await api.post("/verifications", {
        id_doc: idDoc,
        insurance_doc: insurance,
        reg_number: regNumber,
        notes,
      });
      toast.success("Submitted for review — we'll email you within 48h");
      setIdDoc(null); setInsurance(null); setRegNumber(""); setNotes("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const statusBadge = current && (
    <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${
      current.status === "approved" ? "bg-emerald-100 text-emerald-700" :
      current.status === "rejected" ? "bg-red-100 text-red-700" :
      "bg-[rgba(226,109,41,0.12)] text-[var(--brand-orange)]"
    }`} data-testid="verification-status">
      {current.status === "approved" && <CheckCircle size={12} weight="fill" className="inline -mt-0.5 mr-0.5" />}
      {current.status === "pending" && <Clock size={12} weight="fill" className="inline -mt-0.5 mr-0.5" />}
      {current.status === "rejected" && <XIcon size={12} weight="bold" className="inline -mt-0.5 mr-0.5" />}
      {current.status}
    </span>
  );

  return (
    <section className="mt-8 brand-card p-6" data-testid="verification-section">
      <div className="flex items-center gap-3 flex-wrap">
        <ShieldCheck size={28} weight="duotone" className="text-[var(--brand-orange)]" />
        <div className="flex-1">
          <h2 className="font-display font-extrabold text-xl text-[var(--brand-green)]">Verified badge</h2>
          <p className="text-sm text-[var(--text-muted)]">Upload your ID + insurance to get the green Verified badge on your profile. Customers trust verified traders much more.</p>
        </div>
        {statusBadge}
      </div>

      {current?.status === "approved" ? (
        <p className="mt-4 text-sm text-emerald-700">✅ You&apos;re verified. The badge appears on your profile, search cards, and quotes.</p>
      ) : current?.status === "pending" ? (
        <p className="mt-4 text-sm text-[var(--text-muted)]">⏳ Your documents are under review. We aim to approve within 48 hours.</p>
      ) : (
        <form onSubmit={submit} className="mt-5 space-y-4">
          <div>
            <span className="text-xs font-semibold text-[var(--text-muted)]">Photo ID (driving licence or passport) *</span>
            <label className="mt-1 block border-2 border-dashed border-[var(--border-stone)] rounded-md p-4 text-center cursor-pointer hover:bg-[#FAF8F2]" data-testid="id-doc-upload">
              {idDoc ? <img src={idDoc} alt="" className="max-h-32 mx-auto rounded" /> : <span className="text-sm text-[var(--text-muted)]">Tap to upload ID document</span>}
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setIdDoc(await fileToDataUrl(f)); }} />
            </label>
          </div>
          <div>
            <span className="text-xs font-semibold text-[var(--text-muted)]">Public liability insurance certificate (optional but recommended)</span>
            <label className="mt-1 block border-2 border-dashed border-[var(--border-stone)] rounded-md p-4 text-center cursor-pointer hover:bg-[#FAF8F2]" data-testid="insurance-doc-upload">
              {insurance ? <img src={insurance} alt="" className="max-h-32 mx-auto rounded" /> : <span className="text-sm text-[var(--text-muted)]">Tap to upload insurance doc</span>}
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setInsurance(await fileToDataUrl(f)); }} />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold text-[var(--text-muted)]">Registration / scheme number (Gas Safe, NICEIC, NAPIT etc.)</span>
            <input data-testid="reg-number-input" value={regNumber} onChange={(e) => setRegNumber(e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white" placeholder="e.g. Gas Safe 123456" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-[var(--text-muted)]">Notes (optional)</span>
            <textarea data-testid="verification-notes-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-md border border-[var(--border-stone)] bg-white resize-y" />
          </label>
          {current?.status === "rejected" && current.review_reason && (
            <p className="text-sm text-red-700">Previous rejection: {current.review_reason}</p>
          )}
          <button type="submit" disabled={busy} className="btn-accent w-full justify-center" data-testid="verification-submit-btn">
            {busy ? "Submitting…" : "Submit for verification"}
          </button>
        </form>
      )}
    </section>
  );
}
